import { ExpandLess, ExpandMore, SportsBar } from '@mui/icons-material';
import { Avatar, Backdrop, Box, Button, IconButton, ListItemText, Paper } from '@mui/material';
import axios from 'axios';
import { withSnackbar } from 'notistack';
import React, { Component } from 'react';
import './App.css';
import { setAuthToken } from './Hooks/setAuthToken';
import LocationList from './LocationList';
import Map from './Map';
import { Award, Location, Zone } from './Types/Types';

axios.defaults.baseURL = process.env.REACT_APP_API_HOST + '/';
axios.interceptors.response.use((response) => response, (error) => {
  // whatever you want to do with the error
  if (typeof error.response !== 'undefined' && typeof error.response.status !== 'undefined') {
    if (error.response.status === 401) {
      setAuthToken(null);
    }
    if (error.response.status === 403) {
      setAuthToken(null);
    }
  }
  throw error;
});
interface Props {
  enqueueSnackbar: any,
};
interface StateTyping {
  locations: Location[], 
  zones: Zone[],
  center?: Position,
  coords?: Position,
  position?: Location, 
  isDrinking: Boolean, 
  isDrinkingBackdrop: Boolean, 
  response: number, 
  lastDrink: Date|null,
  blurFactor: number,
  isListOpen: Boolean,
  permissions: PermissionName[],
}

interface Position {
  lat: number,
  lng: number
}

class App extends Component<any, StateTyping> {
  coordsReloadInterval : number | null = null;
  mapContainerRef = React.createRef<HTMLDivElement>();

  constructor(props : Props) {
    super(props);

    const locationStr = localStorage.getItem('locations');
    const zoneStr = localStorage.getItem('zones');
    const coords = localStorage.getItem('coords');
    this.state={
      locations: JSON.parse(locationStr ?? '[]'),
      zones: JSON.parse(zoneStr ?? '[]'),
      center: coords ? JSON.parse(coords) : undefined,
      position: undefined,
      isDrinking: false,
      isDrinkingBackdrop: false,
      response: 10,
      lastDrink: null,
      coords: coords ? JSON.parse(coords) : undefined,
      blurFactor: 0,
      isListOpen: true,
      permissions: [],
    };
  }

  componentDidMount() {
    this.load();
    this.loadZones();

    const _self = this;
    _self.updateCoords();
    this.coordsReloadInterval = window.setInterval(() => {
      _self.updateCoords();
    }, 20000);

    this.updateLastDrink();
    this.updatePermissionState();
    setTimeout(() => {
      try {
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(function (position) {
            _self.setState({
              permissions: ['geolocation'],
            });
          })
        }
      } catch (e) {}
    }, 200);
  }

  componentWillUnmount() {
    if (this.coordsReloadInterval) {
      window.clearInterval(this.coordsReloadInterval);
    }
  }

  checkForPermissions = () => {
    const { permissions } = this.state;
    if (permissions.indexOf('geolocation') === -1) {
      this.updatePermissionState();
    }
  }
  
  updatePermissionState = () => {
    const { permissions } = this.state;
    let newPermissions = [...permissions];

    const wanted : PermissionName[] = [
        "geolocation",
    ];
    const self = this;
    if (navigator.permissions && navigator.permissions.query) {
      wanted.forEach((perm : PermissionName) => {
          navigator.permissions.query({name: perm}).then(function(result) {
              if (result.state == 'granted') {
                  if (newPermissions.indexOf(perm) === -1) {
                    newPermissions.push(perm);
                  }
                  self.setState({
                      permissions: newPermissions,
                  }, () => {
                    self.updateCoords();
                  });
              } else if (result.state == 'prompt') {}
          }); 
      });
    } else {
      self.setState({
        permissions: ['geolocation'],
      }, () => {
        self.updateCoords();
      });
    }
  }

  hasPermission = (permission:PermissionName) => {
    const { permissions } = this.state;
    
    return permissions.includes(permission);
  }

  handleOnMapMove = () => {
    this.setState({
      isListOpen: false,
    });
  }

  handleToggleList = () => {
    const { isListOpen: currentIsListOpen } = this.state;
    this.setState({
      isListOpen: !currentIsListOpen,
    });
  }

  updateCoords = () => {
    if (this.hasPermission('geolocation')) {
      const _self = this;
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(function(position) {
          _self.setState({
            coords: {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            }
          }, () => {
            localStorage.setItem('coords', JSON.stringify(_self.state.coords));
          });
        }, function(error) {
          _self.props.enqueueSnackbar(
            <Box sx={{ display: 'flex' }}>
              <ListItemText
                primary={"Is as GPS aktiv?"}
                secondary={"Na kod a Baustöh"}
              />
            </Box>, { variant: "error", autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } });
        });
      } else {
        if (this.coordsReloadInterval) {
          window.clearInterval(this.coordsReloadInterval);
        }
        this.props.enqueueSnackbar(
          <Box sx={{ display: 'flex' }}>
            <ListItemText
              primary={"Koane GPS Berechtigungen"}
              secondary={"Mit dein glumpat weascht des nix wean"}
            />
          </Box>, { variant: "error", autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } });
      }
    }
  }

  load = () => {
    axios.get<Location[]>('/locations')
      .then(res => {
        const locations = res.data;
        localStorage.setItem('locations', JSON.stringify(locations));
        this.setState({
          locations: locations,
        });
      });
  }

  loadZones = () => {
    axios.get<Zone[]>('/zones')
      .then(res => {
        const zones = res.data;
        localStorage.setItem('zones', JSON.stringify(zones));
        this.setState({
          zones: zones,
        });
      });
  }

  setLastDrink = () => {
    localStorage.setItem('last-drink', (new Date()).toString());
    this.updateLastDrink();
  }

  updateLastDrink = () => {
    const lastDrink = localStorage.getItem('last-drink');
    if (lastDrink) {
      this.setState({
        lastDrink: new Date(lastDrink),
      });
    }
  }

  getAwardIcon = (icon : string) => {
    return process.env.PUBLIC_URL + '/awards/' + icon;
  }

  handleAddLocation = () => {
    const { coords } = this.state;

    const _self   = this;
    if (coords) {
      _self.setState({
        response: Math.floor(Math.random() * 10),
        isDrinking: true,
        isDrinkingBackdrop: true,
        blurFactor: 0,
        center: coords,
        position: undefined,
      }, () => {
        _self.setLastDrink();
        axios.post<Award[]>('/user/location', {
          lat: coords.lat,
          lng: coords.lng,
        }).then(res => {
          const awards = res.data ?? [];

          awards.forEach((award : Award) => {
            if (award.id === 5) {
              setTimeout(() => {
                document.dispatchEvent(new CustomEvent('allow-hasselcall'));
              }, 3000);
            }
            this.props.enqueueSnackbar(
              <Box sx={{ display: 'flex' }}>
                <Avatar src={this.getAwardIcon(award.image)} sx={{ alignSelf: 'center', marginRight: 2 }}/>
                <ListItemText
                  primary={award.name}
                  secondary={award.description}
                />
              </Box>, { variant: "success", autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } });
          });

          _self.load();
          setTimeout(() => {
            _self.setState({
              isDrinkingBackdrop: false,
            }, () => {
              document.dispatchEvent(new CustomEvent('update'));
            });
          }, 2500);
          setTimeout(() => {
            _self.setState({
              isDrinking: false,
            });
          }, 60000);
        });
      });
    }
  }

  handleCenterPosition = (e: React.MouseEvent | null, loc : Location) => {
    if (loc) {
      this.setState({
        center: { lat: loc.lat, lng: loc.lng },
        position: loc,
      });
    }
  }

  handleIncreaseBlurFactor = (e : React.MouseEvent) => {
    const { blurFactor } = this.state;
    this.setState({
      blurFactor: blurFactor + 1,
    }, () => {
      if (blurFactor >= 20) {
        this.setState({
          blurFactor: 0,
        }, () => {
          if (!localStorage.getItem('allow-hasselcall')) {
            document.dispatchEvent(new CustomEvent('allow-hasselcall'));
            this.props.enqueueSnackbar(
              <Box sx={{ display: 'flex' }}>
                <ListItemText
                  primary={'Hasselcall freigeschalten!'}
                  secondary={'Teife. Du weaschtn scho brauchn.'}
                />
              </Box>, { variant: "success", autoHideDuration: 3000, anchorOrigin: { vertical: 'bottom', horizontal: 'center' } });
          }
        });
      }
    });
  }

  render(): React.ReactNode {
    const { locations, isListOpen, zones, center, position, isDrinkingBackdrop, blurFactor, coords } = this.state; 

    const texts = [
      'Brav issa!',
      'Hau di weida!',
      'ÖÖÖÖÖÖÖÖÖHHHHH!',
      'Uamatog, öööööh!',
      'Geila boscht.',
      'Legga bierli',
      'Zoi amoi oas!',
      'Hauptsoch bier.',
      'Happy Buamatog!',
      'Schea das di gib.',
      'Prost, Moizeit',
    ];

    const getRandomMessage = () => {    
      const { response } = this.state;  
      return texts[response] ?? texts[10];
    }

    const isDrinkTimeReasonable = () => {
      const { lastDrink, isDrinking } = this.state;
      if (isDrinking) return false;
      if (lastDrink) {
        const diffTime = Math.abs(lastDrink.getTime() - (new Date()).getTime());
        const minutes = Math.floor(diffTime / (1000 * 60)); 
        if (minutes >= 1) {
          return true;
        }
        return false;
      }
      return true;
    }
    
    const getLoaderGif = () => {
      return <img src={process.env.PUBLIC_URL + '/beer-drink.gif'} alt={'beer'} style={{ height: '100%', opacity: 0.8 }}/>;
    }

    const getMapOffsetInPixels = (): number|undefined => {
      if (this.mapContainerRef && this.mapContainerRef.current) {
        const containerHeight = this.mapContainerRef.current.clientHeight;
        return containerHeight * 0.5;
      }
      return undefined;
    }

    return (
      <React.Fragment>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme:any) => theme.zIndex.drawer + 1 }}
          open={isDrinkingBackdrop?true:false}
        >
          {getLoaderGif()}
        </Backdrop>
        <Box flexGrow={1} sx={{ filter: 'blur('+blurFactor+'px)', display: 'flex', flexDirection: 'column', height: '100%' }} ref={this.mapContainerRef}>
          <Map
            current={coords}
            zones={zones}
            center={center}
            position={position}
            locations={locations}
            onClickLocation={this.handleCenterPosition}
            bottomOffset={getMapOffsetInPixels()}
          />
          <Paper sx={{ position: 'absolute', zIndex: 1000, bottom: '0px', width: '100%', height: isListOpen ? '50vh' : '60px', paddingTop: '0px', borderRadius: '20px 20px 0px 0px', boxShadow: '-10px -10px 20px 10px rgba(0,0,0,0.5)' }} onPointerDown={(e) => e.stopPropagation()}>
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {this.hasPermission('geolocation') && <Box sx={{ display: 'grid', placeItems: 'center', width: '100%' }}>
                <Button variant={"contained"} onClick={isDrinkTimeReasonable() ? this.handleAddLocation : this.handleIncreaseBlurFactor} sx={{ opacity: isDrinkTimeReasonable() ? 1 : 0.5, position: 'absolute', top: '-68px', height: '96px', width: '96px', borderRadius: '30px', zIndex: 10, left: '50%', transform: 'translateX(-50%)' }}>
                  <Box sx={{ position: 'absolute', opacity: 0.5 }}><SportsBar className={isDrinkTimeReasonable() ? 'shake-animation-infinite': undefined} sx={{ height: '56px', width: '56px', marginTop: '5px' }} /></Box>
                </Button>
              </Box>}
              <Box flexGrow={1} sx={{ overflowY: 'auto', ...(isListOpen ? { borderRadius: '20px 20px 0px 0px' } : { display: 'none' }) }}>
                <LocationList locations={locations} onClick={this.handleCenterPosition}/>
              </Box>
              <Box sx={{ position: 'absolute', top: 0, width: '100%' }}>
                <Box sx={{ display: 'flex' }}>
                  <Box flexGrow={1}></Box>
                  <Box flexGrow={0}>
                    <IconButton onClick={(e) => this.handleToggleList()}>
                      {isListOpen ? <ExpandMore/> : <ExpandLess/>}
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </React.Fragment>
    );
  }
}
export default withSnackbar(App);
